import { useState } from "react";

import { Checkbox, Input } from "../../../shared/ui";
import {
  MAX_DISPLAY_NAME_CHARS,
  MAX_ROLES_PER_CREATE,
  MAX_SUBJECT_CHARS,
} from "./accessApi";
import type { AccessEnv } from "./accessEnv";
import { isUuid, roleLabel } from "./accessFormat";
import { ChangeReasonSheet } from "./ChangeReasonSheet";
import { useAccessT } from "./useAccessT";

const NO_WHITESPACE = /^\S+$/;

/** Add-operator form: pasted Auth0 user_id, name, optional link, roles. */
export function AddOperatorSheet({
  env,
  onClose,
}: {
  env: AccessEnv;
  onClose: () => void;
}) {
  const t = useAccessT();
  const [subject, setSubject] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [linkedUser, setLinkedUser] = useState("");
  const [roleIds, setRoleIds] = useState<readonly string[]>([]);
  const [touched, setTouched] = useState(false);

  const subjectText = subject.trim();
  const nameText = displayName.trim();
  const linkedText = linkedUser.trim();
  const subjectOk =
    subjectText.length > 0 &&
    subjectText.length <= MAX_SUBJECT_CHARS &&
    NO_WHITESPACE.test(subjectText);
  const nameOk =
    nameText.length > 0 && nameText.length <= MAX_DISPLAY_NAME_CHARS;
  const linkedOk = linkedText === "" || isUuid(linkedText);
  const rolesOk = roleIds.length <= MAX_ROLES_PER_CREATE;
  const grantable = env.roles.filter((role) => role.grantable && role.active);

  const toggleRole = (roleId: string, on: boolean) => {
    setRoleIds((current) =>
      on ? [...current, roleId] : current.filter((id) => id !== roleId),
    );
  };

  return (
    <ChangeReasonSheet
      env={env}
      title={t("admin.access.change.create.title")}
      warning={t("admin.access.change.create.warning")}
      extraValid={subjectOk && nameOk && linkedOk && rolesOk}
      onClose={onClose}
      run={(change) =>
        env.api.createOperator({
          ...change,
          subject: subjectText,
          display_name: nameText,
          linked_user_id: linkedText === "" ? null : linkedText,
          role_ids: [...roleIds],
        })
      }
    >
      <label className="flex min-w-0 flex-col gap-1 text-footnote text-fg-muted">
        {t("admin.access.add.subject")}
        <Input
          value={subject}
          maxLength={MAX_SUBJECT_CHARS}
          className="font-mono"
          aria-invalid={touched && !subjectOk}
          onBlur={() => {
            setTouched(true);
          }}
          onChange={(event) => {
            setSubject(event.target.value);
          }}
        />
        <span className="text-caption">
          {t("admin.access.add.subjectHelp")}
        </span>
        {touched && !subjectOk && (
          <span role="alert" className="text-caption text-error">
            {t("admin.access.add.error.subject")}
          </span>
        )}
      </label>
      <label className="flex min-w-0 flex-col gap-1 text-footnote text-fg-muted">
        {t("admin.access.add.displayName")}
        <Input
          value={displayName}
          maxLength={MAX_DISPLAY_NAME_CHARS}
          aria-invalid={touched && !nameOk}
          onBlur={() => {
            setTouched(true);
          }}
          onChange={(event) => {
            setDisplayName(event.target.value);
          }}
        />
        {touched && !nameOk && (
          <span role="alert" className="text-caption text-error">
            {t("admin.access.add.error.displayName")}
          </span>
        )}
      </label>
      <label className="flex min-w-0 flex-col gap-1 text-footnote text-fg-muted">
        {t("admin.access.add.linkedUser")}
        <Input
          value={linkedUser}
          maxLength={36}
          className="font-mono"
          aria-invalid={!linkedOk}
          onChange={(event) => {
            setLinkedUser(event.target.value);
          }}
        />
        {!linkedOk && (
          <span role="alert" className="text-caption text-error">
            {t("admin.access.add.error.linkedUser")}
          </span>
        )}
      </label>
      <fieldset className="flex flex-col">
        <legend className="text-footnote text-fg-muted">
          {t("admin.access.add.roles")}
        </legend>
        {grantable.length === 0 ? (
          <p className="text-footnote text-fg-muted">
            {t("admin.access.add.noRoles")}
          </p>
        ) : (
          grantable.map((role) => (
            <Checkbox
              key={role.role_id}
              checked={roleIds.includes(role.role_id)}
              onCheckedChange={(on) => {
                toggleRole(role.role_id, on);
              }}
              label={roleLabel(t, role.role_id, role.display_name)}
            />
          ))
        )}
      </fieldset>
    </ChangeReasonSheet>
  );
}
